import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert,
  Avatar,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { 
  ArrowBack, 
  Save, 
  Delete as DeleteIcon
} from '@mui/icons-material';
import { channelsAPI, playlistsAPI } from '../services/api';
import BasicSettingsTab from './tabs/BasicSettingsTab';
import StreamSettingsTab from './tabs/StreamSettingsTab';
import AdvancedSettingsTab from './tabs/AdvancedSettingsTab';
import GeoRestrictionsTab from './tabs/GeoRestrictionsTab';

const ChannelEditPage = () => {
  const { playlistId, channelId, id: routeId } = useParams();
  const navigate = useNavigate();
  // Используем channelId если есть (из плейлиста), иначе routeId (из /channels/:id/edit)
  const actualChannelId = channelId || routeId;
  const isNewChannel = actualChannelId === 'new';
  const id = isNewChannel ? null : actualChannelId;
  const [activeTab, setActiveTab] = useState(0);
  const [channel, setChannel] = useState(null);
  const [formData, setFormData] = useState({
    url: '',
    name: '',
    icon: '',
    frame: '',
    stream: '',
    stream_redirect: '',
    vitrina_config_url: '',
    headers: { 'User-Agent': '', 'Referer': '', 'Origin': '' },
    player_type_mobile: 'frame',
    player_type_tv: 'stream',
    player_type_web: 'frame',
    geo_restrictions: { type: 'world', countries: [] },
    is_active: true,
    sort_order: 1
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Проверяем, есть ли несохраненные изменения
  const hasUnsavedChanges = () => {
    if (isNewChannel || !originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  useEffect(() => {
    console.log('ChannelEditPage useEffect triggered');
    console.log('ID from params:', id);
    console.log('Is new channel:', isNewChannel);
    console.log('Current location:', window.location.pathname);
    
    if (!isNewChannel) {
      console.log('Loading existing channel...');
      loadChannel();
    } else {
      console.log('Creating new channel - calculating next sort order');
      calculateNextSortOrder();
    }
  }, [id]);

  const calculateNextSortOrder = async () => {
    try {
      // Загружаем каналы текущего плейлиста для расчета следующей позиции
      const response = await playlistsAPI.getChannels(playlistId);
      const existingChannels = response.data;
      
      // Следующая позиция = количество существующих каналов + 1 (1-based для UI)
      const nextSortOrder = existingChannels.length + 1;
      
      console.log(`Calculated next sort order: ${nextSortOrder} (existing channels: ${existingChannels.length})`);
      
      // Обновляем formData с правильной позицией
      setFormData(prev => ({
        ...prev,
        sort_order: nextSortOrder
      }));
      
      // Устанавливаем originalData для нового канала
      const newChannelData = {
        ...formData,
        sort_order: nextSortOrder
      };
      setOriginalData(JSON.parse(JSON.stringify(newChannelData)));
      
    } catch (err) {
      console.error('Error calculating next sort order:', err);
      // Fallback к позиции 1 при ошибке
      setFormData(prev => ({ ...prev, sort_order: 1 }));
    }
  };

  const loadChannel = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Loading channel with ID:', id);
      console.log('Current URL:', window.location.href);
      
      // Если канал редактируется в контексте плейлиста, используем новый API
      const response = playlistId 
        ? await playlistsAPI.getChannel(playlistId, id)
        : await channelsAPI.getById(id);
      
      console.log('Channel loaded successfully:', response.data);
      const channelData = response.data;
      setChannel(channelData);
      const formDataObj = {
        url: channelData.url || '',
        name: channelData.name || '',
        icon: channelData.icon || '',
        frame: channelData.frame || '',
        stream: channelData.stream || '',
        stream_redirect: channelData.stream_redirect || '',
        vitrina_config_url: channelData.vitrina_config_url || '',
        headers: (channelData.headers && typeof channelData.headers === 'object') 
          ? channelData.headers 
          : { 'User-Agent': '', 'Referer': '', 'Origin': '' },
        player_type_mobile: channelData.player_type_mobile || 'frame',
        player_type_tv: channelData.player_type_tv || 'stream',
        player_type_web: channelData.player_type_web || 'frame',
        geo_restrictions: channelData.geo_restrictions || { type: 'world', countries: [] },
        is_active: channelData.is_active !== undefined ? channelData.is_active : true,
        sort_order: (channelData.sort_order !== undefined ? channelData.sort_order + 1 : 1)
      };
      setFormData(formDataObj);
      setOriginalData(JSON.parse(JSON.stringify(formDataObj))); // Глубокая копия
      console.log('Form data set successfully');
    } catch (err) {
      console.error('Error loading channel:', err);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      const errorMessage = err.response?.data?.detail || err.message || 'Ошибка загрузки канала';
      setError(`Ошибка загрузки канала: ${errorMessage}`);
      if (err.response?.status === 404) {
        setError('Канал не найден');
      }
      // НЕ делаем редирект при ошибке - пусть пользователь видит ошибку
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Сбрасываем индикатор успешного сохранения при изменении данных
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  const handleHeaderChange = (headerKey, value) => {
    setFormData(prev => ({
      ...prev,
      headers: {
        ...prev.headers,
        [headerKey]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      // Подготавливаем данные для сохранения, конвертируя sort_order из 1-based в 0-based
      const saveData = {
        ...formData,
        sort_order: formData.sort_order - 1 // Конвертируем из UI (1-based) в DB (0-based)
      };
      
      if (isNewChannel) {
        // Если создаём в контексте плейлиста, используем sort_order = 0 для канала
        // чтобы не сдвигать глобальную сортировку, а управлять порядком через playlist_channels
        const channelData = playlistId 
          ? { ...saveData, sort_order: 0 } 
          : saveData;
        
        // Создаём канал
        const response = await channelsAPI.create(channelData);
        const newChannel = response.data;
        
        // Если создаём в контексте плейлиста, добавляем канал в плейлист с нужной позицией
        if (playlistId) {
          await playlistsAPI.addChannel(playlistId, newChannel.id, saveData.sort_order);
        }
        
        navigate(`/playlists/${playlistId}/channels`);
      } else {
        // Если канал редактируется в контексте плейлиста, используем новый API endpoint
        if (playlistId) {
          await playlistsAPI.updateChannel(playlistId, id, saveData);
        } else {
          // Fallback для случаев, когда playlistId не определен
          await channelsAPI.update(id, saveData);
        }
        
        console.log('DEBUG: Before reloading channel data, current formData.sort_order:', formData.sort_order);
        
        // Получаем актуальные данные канала с сервера для синхронизации позиции
        const updatedChannelResponse = playlistId 
          ? await playlistsAPI.getChannel(playlistId, id)
          : await channelsAPI.getById(id);
        
        const updatedChannelData = updatedChannelResponse.data;
        console.log('DEBUG: Updated channel data from server:', updatedChannelData);
        
        // Обновляем formData с актуальными данными, особенно sort_order
        const updatedFormData = {
          ...formData,
          sort_order: (updatedChannelData.sort_order !== undefined ? updatedChannelData.sort_order + 1 : 1)
        };
        
        console.log('DEBUG: Updated formData.sort_order:', updatedFormData.sort_order);
        
        setFormData(updatedFormData);
        setOriginalData(JSON.parse(JSON.stringify(updatedFormData)));
        
        // Показываем уведомление об успешном сохранении
        setSnackbar({ open: true, message: 'Изменения успешно сохранены', severity: 'success' });
        
        // Возвращаемся обратно: в плейлист или на страницу всех каналов
        setTimeout(() => {
          if (playlistId) {
            navigate(`/playlists/${playlistId}/channels`);
          } else {
            navigate('/channels');
          }
        }, 1000); // Даем время показать snackbar
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Произошла ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };



  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setSaving(true);
      await channelsAPI.delete(id);
      navigate(`/playlists/${playlistId}/channels`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления канала');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => playlistId ? navigate(`/playlists/${playlistId}/channels`) : navigate('/channels')} 
          sx={{ mr: 2 }}
        >
          <ArrowBack />
        </IconButton>
        <Avatar
          src={formData.icon}
          sx={{ width: 48, height: 48, mr: 2 }}
        >
          {formData.name.charAt(0)}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4">
            {isNewChannel ? 'Новый канал' : formData.name || 'Редактирование канала'}
            {!isNewChannel && hasUnsavedChanges() && (
              <Typography component="span" color="warning.main" sx={{ ml: 1, fontSize: '0.8em' }}>
                • Несохраненные изменения
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isNewChannel ? 'Создание нового канала' : `ID: ${id}`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Основные настройки" />
          <Tab label="Потоковое вещание" />
          <Tab label="Гео-ограничения" />
          <Tab label="Дополнительно" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <BasicSettingsTab
              formData={formData}
              onInputChange={handleInputChange}
              playlistId={playlistId}
            />
          )}
          {activeTab === 1 && (
            <StreamSettingsTab
              formData={formData}
              onInputChange={handleInputChange}
            />
          )}
          {activeTab === 2 && (
            <GeoRestrictionsTab
              channel={formData}
              onChannelChange={setFormData}
            />
          )}
          {activeTab === 3 && (
            <AdvancedSettingsTab
              formData={formData}
              onInputChange={handleInputChange}
              onHeaderChange={handleHeaderChange}
            />
          )}
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
        {/* Левая часть - кнопки управления каналом (только для существующих каналов) */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isNewChannel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              disabled={saving}
            >
              Удалить
            </Button>
          )}
        </Box>
        
        {/* Правая часть - основные действия */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => playlistId ? navigate(`/playlists/${playlistId}/channels`) : navigate('/channels')}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || (!isNewChannel && !hasUnsavedChanges())}
            color={saveSuccess ? 'success' : (hasUnsavedChanges() ? 'primary' : 'inherit')}
          >
            {saving ? 'Сохранение...' : (isNewChannel ? 'Создать канал' : (saveSuccess ? '✓ Сохранено' : (hasUnsavedChanges() ? 'Сохранить изменения' : 'Нет изменений')))}
          </Button>
        </Box>
      </Box>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить канал "{formData.name}"?
            Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={saving}>
            {saving ? 'Удаление...' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChannelEditPage;
